import { useState } from 'react';
import axios from 'axios';
import { useProjectStore } from '../stores/projectStore';
import { Project } from '../types/vidmastro';

interface UseVideoUploadReturn {
    upload: (file: File) => Promise<void>;
    progress: number;
    status: 'idle' | 'uploading' | 'processing' | 'completed' | 'error';
    error: string | null;
    projectId: string | null;
}

// Ensure API URL is set
const API_URL = '/api'; // Use relative path to avoid CORS issues if proxying, or environment var

export function useVideoUpload(existingProjectId?: string): UseVideoUploadReturn {
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState<UseVideoUploadReturn['status']>('idle');
    const [error, setError] = useState<string | null>(null);
    const [projectId, setProjectId] = useState<string | null>(existingProjectId || null);

    const addProject = useProjectStore(state => state.addProject);

    const upload = async (file: File) => {
        try {
            setStatus('uploading');
            setProgress(0);
            setError(null);

            // 1. Get presigned URL
            const { data: presignedData } = await axios.post(`${API_URL}/projects/upload-url`, {
                title: file.name.replace(/\.[^/.]+$/, ""), // Remove extension
                videoFileName: file.name,
                videoFileSize: file.size,
                projectId: existingProjectId // Pass existing ID if present
            });

            const { projectId: returnedProjectId, uploadUrl, videoKey } = presignedData;
            setProjectId(returnedProjectId);

            // 2. Upload to R2
            await axios.put(uploadUrl, file, {
                headers: {
                    'Content-Type': file.type,
                },
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setProgress(percentCompleted);
                    }
                },
            });

            // 3. Confirm upload and trigger processing
            setStatus('processing');
            await axios.post(`${API_URL}/projects/${returnedProjectId}/upload-complete`);

            // 4. Add to local store (optimistic update or fetch from response)
            // Ideally the complete endpoint returns the created project
            const newProject: Project = {
                id: returnedProjectId,
                userId: 'current-user', // Should get from auth context
                title: file.name,
                status: 'uploading', // Will be updated by polling or socket
                createdAt: Date.now(),
                updatedAt: Date.now(),
                originalVideoKey: videoKey,
            };

            addProject(newProject);
            setStatus('completed');

        } catch (err: any) {
            console.error('Upload failed:', err);
            setError(err.response?.data?.error || err.message || 'Upload failed');
            setStatus('error');
        }
    };

    return { upload, progress, status, error, projectId };
}
